<template>
  <v-dialog v-model="modal" :max-width="$vuetify.display.mdAndDown ? '95%' : '60%'" persistent>
    <v-card>
      <v-toolbar color="primary" dark>
        <v-toolbar-title>
          {{ account?.name || 'Account Products' }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4">
        <template v-if="isLoading">
          <div class="height-loader">
            <progress-loader></progress-loader>
          </div>
        </template>

        <template v-else>
          <div v-if="accountProducts.length > 0" class="table-box">
            <v-data-table
              class="elevation-1 sticky-header striped-rows"
              :items-per-page="50"
              :headers="headers"
              :items="accountProducts"
              item-value="productName"
              dense
              height="600px"
              fixed-header
              style="max-width: 100%; width: 100%; overflow-x: auto"
            >
              <template v-slot:item.actions="{ item }">
                <div class="action-buttons">
                  <v-btn
                    v-if="item?.dashboard !== '' && item.isActive && item?.dashboard"
                    :disabled="!item.isActive"
                    small
                    color="primary"
                    class="mr-2"
                    @click="redirectToDashboard(account?.juid, item?.dashboard)"
                  >
                    Dashboard URL
                  </v-btn>
                  <v-btn
                    v-else-if="item?.dashboard !== '' && item?.dashboard"
                    :disabled="true"
                    small
                    color="primary"
                    class="mr-2"
                    @click="confirmToggleAccountStatus(item.productName, item.isActive)"
                  >
                    Dashboard URL
                  </v-btn>
                  <div v-else></div>

                  <v-btn
                    :disabled="!account?.is_active"
                    v-if="userRole !== 'company_user'"
                    small
                    color="primary"
                    class="mr-2"
                    @click="confirmToggleAccountStatus(item.productName, item.isActive)"
                  >
                    {{ item.isActive ? 'Disable' : 'Enable' }}
                  </v-btn>

                  <v-btn
                    v-if="userRole !== 'company_user' && item.isActive"
                    small
                    color="primary"
                    :disabled="!item?.isActive || !account?.is_active"
                    class="mr-2"
                    @click="goToEdit(item.productId || item.productName)"
                  >
                    Edit
                  </v-btn>
                  <v-btn
                    v-else-if="userRole !== 'company_user'"
                    small
                    color="primary"
                    :disabled="true"
                    class="mr-2"
                    @click="goToEdit(item.productId || item.productName)"
                  >
                    Edit
                  </v-btn>
                  <v-btn :disabled="!account?.is_active || !item.isActive" small color="primary" class="mr-2 "
                    @click="goToView(item.productName)">

                    View
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </div>
          <div v-else class="text-center py-5">
            <p>No products available for this account</p>
          </div>
        </template>
      </v-card-text>

      <!-- Confirmation Dialog -->
      <v-dialog v-model="dialog" max-width="400">
        <v-card>
          <v-card-title>Confirm Action</v-card-title>
          <v-card-text>
            Are you sure you want to {{ selectedProduct.enabled ? 'enable' : 'disable' }} this
            product?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" @click="dialog = false">Cancel</v-btn>
            <v-btn color="primary" :disabled="disableButtonToggle" @click="toggleProductFunc">
              Confirm
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import {
  fetchAccountsProduct,
  fetchAccountProductsAdmin,
  toggleProductForAccount,
  fetchProducts,
  getParentChild,
  fetchAllProductsFromIndex,
} from '@/api/accountsAdmin'
import ProgressLoader from '@/components/ProgressLoader.vue'
import { useUserStore } from '@/stores/userStore'
import Cookies from 'universal-cookie'
import { useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
const { mdAndDown } = useDisplay()

const userStore = useUserStore()
const cookie = new Cookies()
const userRole = ref(userStore?.role)
const role = userStore.role
const router = useRouter()
const props = defineProps({
  account: Object,
  show: Boolean,
})
const disableButtonToggle = ref(false)

const emit = defineEmits(['close'])

const modal = ref(props.show)
const accountProducts = ref([])
const accountProductsDemo = ref([])
const isLoading = ref(true)
const dialog = ref(false)
const selectedProduct = ref(null)

const headers = [
  { title: 'Product Friendly Name', value: 'productFriendlyName', sortable: true },

  { title: 'Product Name', value: 'productName', sortable: true },
  { title: 'Actions', value: 'actions', sortable: false, align: 'end', width: '150px' },
]
const goToView = (productName) => {
  router.push(`/products/view/${productName}`)
}

const loadAccountProducts = async () => {
  try {
    isLoading.value = true

    // Fetch account products and hierarchy index in parallel
    const [data, hierarchyData] = await Promise.all([
      fetchAccountsProduct(props.account?.juid),
      fetchAllProductsFromIndex(),
    ])

    accountProductsDemo.value = data

    // Build a map of product ID -> hierarchy info for fast lookup
    const hierarchyMap = new Map()
    hierarchyData.products.forEach((p: any) => {
      if (p.productId) hierarchyMap.set(p.productId, p)
      if (p.path && p.path !== p.productId) hierarchyMap.set(p.path, p)

      // Also add by simple name for parent lookups
      const simpleName = (p.productId || p.path || '').split('/').pop()
      if (simpleName && simpleName !== p.productId && simpleName !== p.path) {
        if (!hierarchyMap.has(simpleName)) {
          hierarchyMap.set(simpleName, p)
        }
      }
    })

    // Enrich account products with hierarchy info from index
    accountProducts.value = data?.map((product) => {
      const productName = product.productName || product.productId
      const hierarchyInfo = hierarchyMap.get(productName)

      if (hierarchyInfo) {
        return {
          ...product,
          productId: hierarchyInfo.productId || productName,
          productFriendlyName: hierarchyInfo.productFriendlyName || product.productFriendlyName,
        }
      } else {
        return {
          ...product,
          productId: productName,
        }
      }
    })

    console.log('Processed account products:', accountProducts.value)
  } catch (error) {
    console.error('Failed to load account products:', error)
  } finally {
    isLoading.value = false
  }
}

const confirmToggleAccountStatus = (productName, isActive) => {
  selectedProduct.value = {
    accountName: props.account?.folder,
    productName,
    enabled: !isActive,
  }
  dialog.value = true
}

const toggleProductFunc = async () => {
  disableButtonToggle.value = true
  dialog.value = false
  isLoading.value = true

  try {
    const acct = String(selectedProduct.value.accountName)
    const productName = String(selectedProduct.value.productName)

    // Backend now handles recursive child disabling automatically
    await toggleProductForAccount(acct, productName)
    await loadAccountProducts()

    disableButtonToggle.value = false
  } catch (error) {
    console.error('Error toggling product:', error)
    alert('Failed to toggle product')
    disableButtonToggle.value = false
  } finally {
    dialog.value = false
    disableButtonToggle.value = false
    isLoading.value = false
  }
}

const redirectToDashboard = (juid, dashboardUrl) => {
  if (!dashboardUrl) return
  const token = userStore.token
  const baseUrl = dashboardUrl.replace(/\/+$/, '')
  window.open(
    `${baseUrl}/?token=${token}&juid=${juid}&role=${userStore.role}&redirectionDo=true`,
    '_blank'
  )
}

const goToEdit = (productName) => {
  router.push(`/account/edit/${props.account?.folder}/product?query=${productName}`)
  //   emit('close');
}

const closeModal = () => {
  modal.value = false
  emit('close')
}

watch(
  () => props.show,
  (val) => {
    modal.value = val
    if (val) {
      loadAccountProducts()
    }
  }
)

watch(
  () => props.account,
  () => {
    if (props.account) {
      loadAccountProducts()
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.table-box {
  margin-bottom: 20px;
}

.height-loader {
  height: 50vh !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.v-data-table tbody tr:hover) {
  background-color: #f0f0f0 !important;
}

/* Add this to your style section */
:deep(.v-data-table .inactive-row) {
  background-color: rgba(0, 0, 0, 0.774) !important;
}

:deep(.v-data-table .inactive-row > td) {
  color: #999 !important;
  opacity: 0.8;
}

:deep(.v-data-table .inactive-row:hover > td) {
  background-color: rgba(15, 4, 4, 0.887) !important;
}

/* Optional: Add "Inactive" indicator for mobile view */
:deep(.v-data-table .inactive-row .v-data-table__mobile-row:first-child::after) {
  content: ' (Inactive)';
  color: #f44336;
  font-size: 0.8em;
}

:deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  /* Match your theme */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.striped-rows .v-data-table__tr:nth-child(even)) {
  background-color: #f8f9fa;
  /* Very light gray - adjust to match your theme */
}

/* Hover effect */
:deep(.striped-rows .v-data-table__tr:hover) {
  background-color: #e9ecef !important;
  /* Slightly darker on hover */
}

/* Ensure text remains readable on striped rows */
:deep(.striped-rows .v-data-table__tr) {
  color: inherit;
  /* Inherits from parent */
}

:deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  /* Match your theme */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
